import React, { useEffect, useState } from 'react'
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  TooltipProvider,
} from '../ui/tooltip'
import { AlertTriangle, Bell } from 'lucide-react'
import { ActivityState } from '@/redux/slices/timerConfigurationSlice'
import { FIVE_MIN_SECONDS, ONE_MIN_SECONDS } from '@/constants/Timeline'
import { cn } from '@/lib/utils'
interface CountdownTimerProps {
  endTime: string | Date
  onComplete?: () => void
  showWarning: boolean
  status: string
  enableFiveMinuteVisual?: boolean
  enableOneMinuteVisual?: boolean
}

const CountdownTimer: React.FC<CountdownTimerProps> = ({
  endTime,
  onComplete,
  showWarning,
  status,
  enableFiveMinuteVisual = false,
  enableOneMinuteVisual = true,
}) => {
  const calculateTimeLeft = () => {
    if (status === ActivityState.Completed || status === ActivityState.Paused)
      return null

    const end = typeof endTime === 'string' ? new Date(endTime) : endTime
    const now = new Date()
    const difference = end.getTime() - now.getTime()

    return difference > 0 ? difference : 0
  }

  const [timeLeft, setTimeLeft] = useState<number | null>(null)

  useEffect(() => {
    const leftTime = calculateTimeLeft()
    setTimeLeft(leftTime)
  }, [endTime, status])

  const showOneMinuteWarning =
    showWarning &&
    enableOneMinuteVisual &&
    timeLeft !== null &&
    timeLeft <= ONE_MIN_SECONDS * 1000

  const showFiveMinuteWarning =
    showWarning &&
    enableFiveMinuteVisual &&
    timeLeft !== null &&
    timeLeft <= FIVE_MIN_SECONDS * 1000 &&
    !showOneMinuteWarning

  useEffect(() => {
    if (status === ActivityState.Paused || status === ActivityState.Completed) {
      return
    }

    if (timeLeft !== null && timeLeft <= 0) {
      onComplete?.()
      return
    }

    const timer = setInterval(() => {
      setTimeLeft(calculateTimeLeft())
    }, 1000)

    return () => {
      clearInterval(timer)
    }
  }, [timeLeft, status])

  const formatTime = (milliseconds: number | null): string => {
    if (milliseconds === null) return '--:--'

    const seconds = Math.floor(milliseconds / 1000)

    const hours = Math.floor(seconds / 3600)
    const minutes = Math.floor((seconds % 3600) / 60)
    const remainingSeconds = Math.floor(seconds % 60)

    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`
    }
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`
  }

  return (
    <span
      className={cn(
        showOneMinuteWarning && 'text-red-500',
        showFiveMinuteWarning && 'text-amber-600'
      )}
    >
      <div className='flex items-center ms-1'>
        {formatTime(timeLeft)}{' '}
        {showFiveMinuteWarning && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Bell className='h-4 w-4 text-amber-600 ms-2' />
              </TooltipTrigger>
              <TooltipContent>
                Five minutes or less remaining in this phase.
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
        {showOneMinuteWarning && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <AlertTriangle className='h-4 w-4 text-red-500 ms-2' />
              </TooltipTrigger>
              <TooltipContent>
                Less than one minute remaining in this phase.
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
      </div>
    </span>
  )
}

export default CountdownTimer
